import { motion } from "framer-motion";
import { ArrowRight, Mail, ShieldCheck, Smartphone } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useState } from "react";
import AuthShell from "../components/AuthShell";

const Register = () => {
  const navigate = useNavigate();
  const [method, setMethod] = useState("otp");
  const [contact, setContact] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const nextErrors = {};

    if (method === "otp") {
      if (!/^\d{10}$/.test(contact)) {
        nextErrors.contact = "Enter a valid 10-digit mobile number.";
      }
    } else if (!/^\S+@\S+\.\S+$/.test(contact)) {
      nextErrors.contact = "Enter a valid email address.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleContinue = (event) => {
    event.preventDefault();
    if (!validate()) {
      return;
    }

    setLoading(true);

    if (method === "otp") {
      navigate(`/verify-phone?flow=register&phone=${encodeURIComponent(contact)}`);
      return;
    }

    localStorage.setItem("pendingRegisterEmail", contact);
    toast.info("Email-first signup is staged. We will still verify your phone next.");
    navigate("/verify-phone?flow=register");
  };

  return (
    <AuthShell
      title="Create your JanShakti account"
      subtitle="A clear, modern signup flow built for Indian voters. Choose your path, verify identity, and complete your civic profile."
      step={1}
    >
      <form onSubmit={handleContinue} className="space-y-6">
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            {
              key: "otp",
              title: "OTP Verification",
              description: "Fastest and recommended for secure onboarding.",
              icon: Smartphone,
            },
            {
              key: "email",
              title: "Email First",
              description: "Collect your email now, then finish with phone verification.",
              icon: Mail,
            },
          ].map((option) => (
            <motion.button
              key={option.key}
              type="button"
              whileHover={{ y: -2, scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => {
                setMethod(option.key);
                setErrors({});
              }}
              className={`rounded-3xl border p-5 text-left transition ${
                method === option.key
                  ? "border-orange-400 bg-orange-50 shadow-lg shadow-orange-100/80"
                  : "border-slate-200 bg-white hover:border-orange-200"
              }`}
            >
              <option.icon className="h-5 w-5 text-orange-500" />
              <h3 className="mt-4 text-lg font-bold text-slate-900">{option.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{option.description}</p>
            </motion.button>
          ))}
        </div>

        <div className="rounded-3xl border border-slate-200 bg-slate-50/70 p-5">
          <label className="mb-3 block text-sm font-semibold text-slate-700">
            {method === "otp" ? "Mobile Number" : "Email Address"}
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-slate-400">
              {method === "otp" ? <Smartphone className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
            </span>
            <input
              value={contact}
              onChange={(event) => {
                const nextValue =
                  method === "otp"
                    ? event.target.value.replace(/\D/g, "")
                    : event.target.value;
                setContact(nextValue);
              }}
              placeholder={method === "otp" ? "Enter 10-digit mobile number" : "Enter your email"}
              className={`w-full rounded-2xl border bg-white py-4 pl-12 pr-4 text-sm outline-none transition ${
                errors.contact
                  ? "border-rose-300 focus:border-rose-400"
                  : "border-slate-200 focus:border-orange-400"
              }`}
              maxLength={method === "otp" ? 10 : undefined}
            />
          </div>
          {errors.contact && <p className="mt-2 text-sm text-rose-500">{errors.contact}</p>}
        </div>

        <div className="rounded-3xl border border-emerald-100 bg-emerald-50/80 p-4 text-sm text-emerald-800">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-0.5 h-4 w-4" />
            <p>
              Your phone is used only for secure verification and civic accountability. You will see clear progress through OTP and profile completion.
            </p>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-green-500 px-5 py-4 text-sm font-semibold text-white shadow-xl shadow-orange-200/60 transition hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {loading ? (
            <span className="inline-flex h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          ) : (
            <ArrowRight className="h-4 w-4" />
          )}
          Continue
        </button>

        <p className="text-center text-sm text-slate-600">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-orange-600 hover:text-orange-700">
            Login
          </Link>
        </p>
      </form>
    </AuthShell>
  );
};

export default Register;
